import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Mic, ShieldCheck } from "lucide-react";
import { useApply } from "@/context";
import HeroCarousel from "@/components/HeroCarousel";

const LINES = ["The Dawn of", "India's First", "AI-Enabled State."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + i * 0.14 },
  }),
};

export default function Hero() {
  const { openApply } = useApply();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-light-gray pt-32 pb-16 md:pt-40 md:pb-24"
    >
      {/* soft ambient shapes */}
      <div className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-emerald-green/10 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full bg-vibrant-orange/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-green/30 bg-emerald-green/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-emerald-green"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-green" />
            Pilot Program — Jharkhand 2026
          </motion.div>

          <h1 className="font-heading text-5xl font-extrabold leading-[0.95] tracking-tight text-deep-trust-blue sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {i === 2 ? (
                    <span className="text-emerald-green">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-deep-trust-blue/70 md:text-lg"
          >
            We are replacing manual labor with digital innovation. Through
            <span className="font-semibold text-deep-trust-blue"> voice-first education</span> in your
            own language, every learner — from farm to factory — becomes a creator of technology.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <button
              data-testid="join-pilot-btn"
              onClick={() => openApply()}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-vibrant-orange px-8 py-4 text-base font-semibold text-white shadow-xl shadow-vibrant-orange/30 transition-all duration-300 hover:-translate-y-1 hover:bg-orange-600"
            >
              Join the Pilot Program
              <ArrowUpRight size={20} className="transition-transform group-hover:rotate-45" />
            </button>
            <a
              href="#modules"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-deep-trust-blue/15 px-8 py-4 text-base font-semibold text-deep-trust-blue transition-colors hover:bg-deep-trust-blue hover:text-white"
            >
              Explore Modules
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-6 text-sm text-deep-trust-blue/70"
          >
            <span className="inline-flex items-center gap-2"><Mic size={16} className="text-emerald-green" /> 100% Voice-First</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-green" /> Placement Guarantee</span>
          </motion.div>
        </div>

        {/* Deliberately treated hero carousel — clipped frame + spotlight, cycles through beneficiaries */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <HeroCarousel imgY={imgY} imgScale={imgScale} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
